// PINBALL PANIC (§6.3, §12.5): a neon table over a grid abyss. The playfield is one slab whose top
// is painted on a canvas (indigo, grid, lane arrows, rollover circles, the ball-loop lane, gap
// chevrons, the DRAIN) plus an insert-light id map: the shader lights inserts by id and group, so
// every light chases on the beat in one draw call (§11.4: lane lights chase, flipper lights blink 2
// beats before each fire, plunger lane flashes before BALL LAUNCH, bumper halos pulse). Chrome
// rails with red gap caps, rubber slingshots, five pop bumpers (instanced; flash white and scale
// 1.15 on BUMPER), two neon-edged flippers posed from `view.hazard` with an afterimage, chrome
// giant pinballs reflecting a neon environment, the cabinet on chrome legs, and a backbox whose
// backglass carries a dot-matrix score reel that shows the live kill feed.
import {
  AdditiveBlending, BufferGeometry, Color, CylinderGeometry, DoubleSide, DynamicDrawUsage, EquirectangularReflectionMapping, ExtrudeGeometry,
  Float32BufferAttribute, Group, InstancedBufferAttribute, InstancedMesh, Matrix4, Mesh, MeshBasicMaterial, MeshStandardMaterial, PlaneGeometry,
  Quaternion, Shape, ShaderMaterial, SphereGeometry, TorusGeometry, UniformsLib, UniformsUtils, Vector3, type Texture,
} from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { EV } from '@shared/events';
import { Cause, RoundPhase } from '@shared/ids';
import { SolidKind, SolidTag, createSolid, type Solid } from '@shared/maps/mapTypes';
import { TUNING } from '@shared/maps/pinball';
import { finishBatch } from '../MapView';
import { Batch, hullGeometry, mergeKeep, polygonShape, seeded, slab, sweep, type ProfilePoint } from '../kit/geometry';
import { outlineMaterial, surfaceMaterial } from '../kit/materials';
import { canvasTexture, DISPLAY_FONT, liveTexture, roundRect, starPath, type Ctx2D } from '../kit/textures';
import { B, type Arena, type ArenaFrame, type ArenaKit, type ArenaLayout, type DebugHazard } from './types';

const INDIGO = '#1B1440';
const MAGENTA = '#FF3EC8';
const CYAN = '#2FF3FF';
const GRID = '#7A3CFF';
const DANGER = '#FF4D4D';
const YELLOW = '#FFC93C';
const INK = '#2A1A3A';
const DEG = Math.PI / 180;
const SLAB_DEPTH = 0.45;
const CABINET_DEPTH = 2.0;
const F = TUNING.flipper;
const BALL_R = 1.2;
/** The giant ball's closed loop (§6.3, from the map module): the lane the inserts are painted along. */
export const BALL_LOOP: readonly (readonly [number, number])[] = [...TUNING.ball.path, TUNING.ball.path[0]].map((p) => [p.x, p.y] as const);
const BEATS_PER_BAR = 4;

/** Insert-light groups painted into the id map (blue channel). */
export const Insert = { LANE: 1, PLUNGER: 2, ARROW: 3, BUMPER: 4, FLIPPER: 5, DANGER: 6, ROLLOVER: 7, SLING: 8 } as const;

// ---- Playfield canvas ------------------------------------------------------------------------

const PF = { x0: -9.4, y0: -6.4, w: 18.8, h: 12.8 } as const;

interface Painter { g: Ctx2D; ppm: number; X(x: number): number; Y(y: number): number }

function painter(g: Ctx2D, ppm: number): Painter {
  return { g, ppm, X: (x) => (x - PF.x0) * ppm, Y: (y) => (y - PF.y0) * ppm };
}

/** Paints `shape` both on the art (in `color`) and on the id map (id, full coverage, group). */
type Fill = (p: Painter, idPass: boolean) => void;
interface InsertDef { group: number; id: number; color: string; draw: Fill }

function chevron(p: Painter, x: number, y: number, size: number, angle: number): void {
  const g = p.g;
  g.save();
  g.translate(p.X(x), p.Y(y));
  g.rotate(angle);
  const s = size * p.ppm;
  g.beginPath();
  g.moveTo(0, -s * 0.55);
  g.lineTo(s * 0.6, s * 0.15);
  g.lineTo(s * 0.6, s * 0.5);
  g.lineTo(0, -s * 0.2);
  g.lineTo(-s * 0.6, s * 0.5);
  g.lineTo(-s * 0.6, s * 0.15);
  g.closePath();
  g.fill();
  g.restore();
}

function circle(p: Painter, x: number, y: number, r: number): void {
  p.g.beginPath();
  p.g.arc(p.X(x), p.Y(y), r * p.ppm, 0, Math.PI * 2);
  p.g.fill();
}

function loopPoints(spacing: number): { x: number; y: number; a: number }[] {
  const out: { x: number; y: number; a: number }[] = [];
  let carry = spacing * 0.5;
  for (let i = 1; i < BALL_LOOP.length; i++) {
    const [ax, ay] = BALL_LOOP[i - 1];
    const [bx, by] = BALL_LOOP[i];
    const len = Math.hypot(bx - ax, by - ay);
    const a = Math.atan2(by - ay, bx - ax);
    let d = carry;
    while (d < len) {
      out.push({ x: ax + ((bx - ax) * d) / len, y: ay + ((by - ay) * d) / len, a });
      d += spacing;
    }
    carry = d - len;
  }
  return out;
}

function insertDefs(): InsertDef[] {
  const defs: InsertDef[] = [];
  // The giant ball's loop: round lane lights, chasing in the ball's direction.
  loopPoints(1.25).forEach((pt, i) => {
    const plunger = pt.x > 7 && pt.y > -4.2 && pt.y < 2.4;
    defs.push({
      group: plunger ? Insert.PLUNGER : Insert.LANE, id: i + 1, color: plunger ? YELLOW : CYAN,
      draw: (p) => {
        circle(p, pt.x, pt.y, 0.2);
      },
    });
  });
  // Center arrows pointing up the table.
  [4.6, 3.75, 1.95, 1.1].forEach((y, i) => defs.push({ group: Insert.ARROW, id: i + 1, color: MAGENTA, draw: (p) => chevron(p, 0, y, 0.85, 0) }));
  // Bumper halos.
  TUNING.bumper.positions.forEach((b, i) => defs.push({
    group: Insert.BUMPER, id: i + 1, color: i % 2 === 0 ? MAGENTA : CYAN,
    draw: (p, idPass) => {
      const g = p.g;
      g.save();
      g.lineWidth = 0.14 * p.ppm;
      g.strokeStyle = g.fillStyle as string;
      g.beginPath();
      g.arc(p.X(b.x), p.Y(b.y), 1.12 * p.ppm, 0, Math.PI * 2);
      g.stroke();
      for (let k = 0; k < 8; k++) {
        const a = (k / 8) * Math.PI * 2 + 0.2;
        g.beginPath();
        g.arc(p.X(b.x + Math.cos(a) * 1.42), p.Y(b.y + Math.sin(a) * 1.42), 0.08 * p.ppm, 0, Math.PI * 2);
        g.fill();
      }
      g.restore();
      void idPass;
    },
  }));
  // Flipper arrows (blink before each fire).
  for (const s of [-1, 1]) {
    defs.push({
      group: Insert.FLIPPER, id: s < 0 ? 1 : 2, color: YELLOW,
      draw: (p) => {
        const g = p.g;
        g.beginPath();
        g.moveTo(p.X(s * 1.0), p.Y(4.0));
        g.lineTo(p.X(s * 2.4), p.Y(3.15));
        g.lineTo(p.X(s * 2.2), p.Y(4.2));
        g.closePath();
        g.fill();
      },
    });
  }
  // Danger chevrons at every open edge: the top gap, the side gaps and the drain.
  for (let k = 0; k < 3; k++) {
    defs.push({ group: Insert.DANGER, id: 1, color: DANGER, draw: (p) => chevron(p, (k - 1) * 0.9, -5.55, 0.6, 0) });
    for (const s of [-1, 1]) {
      defs.push({ group: Insert.DANGER, id: 2, color: DANGER, draw: (p) => chevron(p, s * 8.55, (k - 1) * 0.75, 0.55, s * Math.PI / 2) });
    }
    defs.push({ group: Insert.DANGER, id: 3, color: DANGER, draw: (p) => chevron(p, (k - 1) * 1.3, 5.35, 0.6, Math.PI) });
  }
  // B-O-N-K rollovers.
  ['B', 'O', 'N', 'K'].forEach((ch, i) => defs.push({
    group: Insert.ROLLOVER, id: i + 1, color: i % 2 === 0 ? CYAN : MAGENTA,
    draw: (p, idPass) => {
      const x = -2.4 + i * 1.6;
      const y = -3.75;
      circle(p, x, y, 0.42);
      if (!idPass) {
        const g = p.g;
        g.save();
        g.fillStyle = INK;
        g.font = `${Math.round(0.5 * p.ppm)}px ${DISPLAY_FONT}`;
        g.textAlign = 'center';
        g.textBaseline = 'middle';
        g.fillText(ch, p.X(x), p.Y(y) + 0.03 * p.ppm);
        g.restore();
      }
    },
  }));
  // Slingshot strips inside the slingshot rails.
  for (const s of [-1, 1]) {
    defs.push({
      group: Insert.SLING, id: s < 0 ? 1 : 2, color: MAGENTA,
      draw: (p) => {
        const g = p.g;
        g.beginPath();
        g.moveTo(p.X(s * 8.35), p.Y(3.25));
        g.lineTo(p.X(s * 3.4), p.Y(5.4));
        g.lineTo(p.X(s * 4.3), p.Y(4.55));
        g.lineTo(p.X(s * 7.6), p.Y(3.1));
        g.closePath();
        g.fill();
      },
    });
  }
  return defs;
}

function paintPlayfield(g: Ctx2D, w: number, h: number, ppm: number, defs: InsertDef[]): void {
  const p = painter(g, ppm);
  // Base: indigo with a lighter heart.
  const grad = g.createRadialGradient(w / 2, h * 0.45, 0, w / 2, h * 0.45, w * 0.6);
  grad.addColorStop(0, '#2A1F63');
  grad.addColorStop(0.6, INDIGO);
  grad.addColorStop(1, '#120D30');
  g.fillStyle = grad;
  g.fillRect(0, 0, w, h);
  // Grid.
  g.strokeStyle = GRID;
  for (let x = Math.ceil(PF.x0); x <= PF.x0 + PF.w; x++) {
    g.globalAlpha = x % 3 === 0 ? 0.28 : 0.12;
    g.lineWidth = x % 3 === 0 ? 3 : 2;
    g.beginPath();
    g.moveTo(p.X(x), 0);
    g.lineTo(p.X(x), h);
    g.stroke();
  }
  for (let y = Math.ceil(PF.y0); y <= PF.y0 + PF.h; y++) {
    g.globalAlpha = y % 3 === 0 ? 0.28 : 0.12;
    g.lineWidth = y % 3 === 0 ? 3 : 2;
    g.beginPath();
    g.moveTo(0, p.Y(y));
    g.lineTo(w, p.Y(y));
    g.stroke();
  }
  g.globalAlpha = 1;
  // The ball lane: a dark channel with neon edges.
  g.lineCap = 'round';
  g.lineJoin = 'round';
  const lane = (width: number, style: string): void => {
    g.strokeStyle = style;
    g.lineWidth = width * ppm;
    g.beginPath();
    BALL_LOOP.forEach(([x, y], i) => (i === 0 ? g.moveTo(p.X(x), p.Y(y)) : g.lineTo(p.X(x), p.Y(y))));
    g.stroke();
  };
  lane(1.08, 'rgba(47, 243, 255, 0.55)');
  lane(0.96, '#0E0A2A');
  // Logo in the middle of the bumper field.
  g.save();
  g.translate(p.X(0), p.Y(-1.0));
  g.font = `${Math.round(0.95 * ppm)}px ${DISPLAY_FONT}`;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.lineJoin = 'round';
  g.strokeStyle = CYAN;
  g.lineWidth = 0.12 * ppm;
  g.strokeText('SHOVE OFF!', 0, 0);
  g.fillStyle = MAGENTA;
  g.fillText('SHOVE OFF!', 0, 0);
  g.restore();
  // Labels.
  const label = (text: string, x: number, y: number, size: number, color: string, angle = 0): void => {
    g.save();
    g.translate(p.X(x), p.Y(y));
    g.rotate(angle);
    g.font = `${Math.round(size * ppm)}px ${DISPLAY_FONT}`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillStyle = color;
    g.fillText(text, 0, 0);
    g.restore();
  };
  label('DRAIN', 0, 5.75, 0.36, DANGER);
  label('LAUNCH', 8.35, -1.5, 0.3, YELLOW, -Math.PI / 2);
  label('PINBALL PANIC', 0, 0.15, 0.3, CYAN);
  // Stars scattered between features.
  const rnd = seeded(77);
  g.fillStyle = '#FFFFFF';
  for (let i = 0; i < 40; i++) {
    const x = PF.x0 + rnd() * PF.w;
    const y = PF.y0 + rnd() * PF.h;
    g.globalAlpha = 0.15 + rnd() * 0.25;
    starPath(g, p.X(x), p.Y(y), (0.05 + rnd() * 0.07) * ppm);
    g.fill();
  }
  g.globalAlpha = 1;
  // Inserts in their (unlit) colors.
  for (const d of defs) {
    g.fillStyle = d.color;
    d.draw(p, false);
  }
}

function paintIds(g: Ctx2D, w: number, h: number, ppm: number, defs: InsertDef[]): void {
  const p = painter(g, ppm);
  g.fillStyle = '#000000';
  g.fillRect(0, 0, w, h);
  for (const d of defs) {
    g.fillStyle = `rgb(${d.id}, 255, ${d.group})`;
    d.draw(p, true);
  }
}

const PLAYFIELD_PARS = /* glsl */ `
uniform sampler2D uArt;
uniform sampler2D uIds;
uniform float uLaneStep;
uniform float uArrowStep;
uniform float uRollStep;
uniform float uPulse;
uniform float uFlipWarn;
uniform float uLaunch;
uniform float uBump[5];
uniform vec4 uBalls;
uniform vec2 uBallOn;
`;

const PLAYFIELD_UV = /* glsl */ `
  vec2 pfUv = vec2((envObj.x - ${PF.x0.toFixed(2)}) / ${PF.w.toFixed(2)}, 1.0 - (envObj.z - ${PF.y0.toFixed(2)}) / ${PF.h.toFixed(2)});
`;

const PLAYFIELD_ALBEDO = /* glsl */ `
  ${PLAYFIELD_UV}
  if (envNo.y > 0.5) {
    vec4 art = texture2D(uArt, pfUv);
    float mask = texture2D(uIds, pfUv).g;
    albedo = mix(art.rgb, art.rgb * 0.4, mask);
  } else {
    // Cabinet rail: black chrome with a neon pinstripe.
    albedo = vec3(0.06, 0.04, 0.13);
  }
`;

const PLAYFIELD_EMISSIVE = /* glsl */ `
  ${PLAYFIELD_UV}
  if (envNo.y > 0.5) {
    vec4 ids = texture2D(uIds, pfUv);
    float id = floor(ids.r * 255.0 + 0.5);
    float grp = floor(ids.b * 255.0 + 0.5);
    float mask = ids.g;
    float lit = 0.0;
    if (grp == 1.0 || grp == 2.0) {
      lit = 1.0 - step(0.5, mod(id - uLaneStep, 4.0));
      lit = max(lit, 0.35 * (1.0 - step(0.5, mod(id - uLaneStep + 1.0, 4.0))));
      for (int i = 0; i < 2; i++) {
        vec2 b = i == 0 ? uBalls.xy : uBalls.zw;
        float on = i == 0 ? uBallOn.x : uBallOn.y;
        lit = max(lit, on * (1.0 - smoothstep(1.2, 3.2, length(envObj.xz - b))));
      }
      if (grp == 2.0) lit = mix(lit, step(0.5, fract(uTime * 6.0)) * 1.6, uLaunch);
    } else if (grp == 3.0) {
      lit = 1.0 - step(0.5, abs(id - 1.0 - uArrowStep));
      lit = max(lit, 0.3);
    } else if (grp == 4.0) {
      int bi = int(id) - 1;
      float f = 0.0;
      for (int i = 0; i < 5; i++) if (i == bi) f = uBump[i];
      lit = 0.35 + 0.65 * uPulse + 1.6 * f;
    } else if (grp == 5.0) {
      lit = 0.2 + uFlipWarn * 1.5;
    } else if (grp == 6.0) {
      lit = 0.45 + 0.4 * sin(uTime * 5.0 + id);
    } else if (grp == 7.0) {
      lit = 1.0 - step(0.5, abs(id - 1.0 - uRollStep));
      lit = max(lit, 0.25);
    } else if (grp == 8.0) {
      lit = 0.3 + 0.7 * uPulse;
    }
    vec3 art = texture2D(uArt, pfUv).rgb;
    glow += art * mask * (0.15 + 1.35 * lit);
  } else {
    float stripe = 1.0 - smoothstep(0.02, 0.05, abs(envObj.y + ${(SLAB_DEPTH * 0.5).toFixed(3)}));
    glow += vec3(1.0, 0.24, 0.78) * stripe * 1.2;
  }
`;

// ---- Neon environment for the chrome ball --------------------------------------------------

function paintNeonEnv(g: Ctx2D, w: number, h: number): void {
  const grad = g.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, '#3A1C6E');
  grad.addColorStop(0.45, '#120B2E');
  grad.addColorStop(0.55, '#1B1440');
  grad.addColorStop(1, '#0B0820');
  g.fillStyle = grad;
  g.fillRect(0, 0, w, h);
  // Neon tubes around the horizon and a soft white key.
  const band = (y: number, color: string, thick: number): void => {
    g.fillStyle = color;
    g.fillRect(0, y, w, thick);
  };
  band(h * 0.38, MAGENTA, 6);
  band(h * 0.46, CYAN, 4);
  band(h * 0.6, GRID, 8);
  for (let i = 0; i < 12; i++) {
    g.fillStyle = i % 2 === 0 ? MAGENTA : CYAN;
    g.fillRect((i / 12) * w + 6, h * 0.2, 5, h * 0.25);
  }
  // A small, crisp key (a softbox): a big soft one turns the chrome ball into a white glare.
  const key = g.createRadialGradient(w * 0.62, h * 0.18, 0, w * 0.62, h * 0.18, h * 0.09);
  key.addColorStop(0, 'rgba(255,255,255,1)');
  key.addColorStop(0.55, 'rgba(255,240,250,0.85)');
  key.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = key;
  g.fillRect(0, 0, w, h);
}

// ---- Score reel (dot-matrix display) --------------------------------------------------------

const DMD_W = 512;
const DMD_H = 112;
const VERBS: Readonly<Record<number, string>> = {
  [Cause.SHOVE]: 'BONKED', [Cause.THROW]: 'YEETED', [Cause.GLOVE]: 'POWED', [Cause.BOMB]: 'KABOOMED', [Cause.PEEL]: 'SLIPPED',
  [Cause.PLUNGER]: 'PLUNGED', [Cause.STOMP]: 'STOMPED', [Cause.PEBBLE]: 'PEBBLED', [Cause.BUMPER]: 'PINBALLED', [Cause.FLIPPER]: 'PINBALLED',
  [Cause.PINBALL]: 'PINBALLED', [Cause.SWEEPER]: 'SWEPT', [Cause.FLAME]: 'TOASTED', [Cause.CUP]: 'SPUN OUT', [Cause.POTATO]: 'POTATOED',
  [Cause.BODY]: 'BOOPED',
};

function paintDmd(g: Ctx2D, w: number, h: number, top: string, bottom: string, hot: boolean): void {
  g.fillStyle = '#000000';
  g.fillRect(0, 0, w, h);
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillStyle = hot ? '#FFFFFF' : MAGENTA;
  g.font = `${Math.round(h * 0.42)}px ${DISPLAY_FONT}`;
  g.fillText(top, w / 2, h * 0.32, w * 0.94);
  g.fillStyle = CYAN;
  g.font = `${Math.round(h * 0.3)}px ${DISPLAY_FONT}`;
  g.fillText(bottom, w / 2, h * 0.76, w * 0.94);
}

const DMD_FRAGMENT = /* glsl */ `
uniform sampler2D uMap;
uniform float uTime;
uniform float uHot;
varying vec2 vUv;
#include <common>
#include <fog_pars_fragment>
void main() {
  vec2 grid = vec2(${DMD_W / 4}.0, ${DMD_H / 4}.0);
  vec2 cell = (floor(vUv * grid) + 0.5) / grid;
  vec3 c = texture2D(uMap, cell).rgb;
  vec2 f = fract(vUv * grid) - 0.5;
  float dotm = 1.0 - smoothstep(0.28, 0.42, length(f));
  float lum = max(c.r, max(c.g, c.b));
  vec3 off = vec3(0.09, 0.05, 0.14);
  vec3 col = mix(off, c * (1.6 + uHot * 1.5), step(0.08, lum)) * dotm;
  col += vec3(0.02, 0.0, 0.05);
  gl_FragColor = vec4(col, 1.0);
  #include <colorspace_fragment>
  #include <fog_fragment>
}`;

const SIMPLE_VERTEX = /* glsl */ `
varying vec2 vUv;
#include <common>
#include <fog_pars_vertex>
void main() {
  vUv = uv;
  vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * mvPosition;
  #include <fog_vertex>
}`;

function paintBackglass(g: Ctx2D, w: number, h: number): void {
  const grad = g.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, '#2B0F5E');
  grad.addColorStop(1, '#0D0826');
  g.fillStyle = grad;
  g.fillRect(0, 0, w, h);
  // Sunburst rays.
  g.save();
  g.translate(w / 2, h * 0.62);
  for (let i = 0; i < 24; i++) {
    g.rotate((Math.PI * 2) / 24);
    g.fillStyle = i % 2 === 0 ? 'rgba(255, 62, 200, 0.16)' : 'rgba(47, 243, 255, 0.1)';
    g.beginPath();
    g.moveTo(0, 0);
    g.lineTo(w, -w * 0.08);
    g.lineTo(w, w * 0.08);
    g.closePath();
    g.fill();
  }
  g.restore();
  // Neon frame.
  g.lineWidth = w * 0.014;
  g.strokeStyle = CYAN;
  roundRect(g, w * 0.03, h * 0.04, w * 0.94, h * 0.92, w * 0.04);
  g.stroke();
  g.lineWidth = w * 0.007;
  g.strokeStyle = MAGENTA;
  roundRect(g, w * 0.055, h * 0.085, w * 0.89, h * 0.83, w * 0.03);
  g.stroke();
  // Title lettering: chunky with a neon glow.
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.lineJoin = 'round';
  const title = (text: string, y: number, size: number, fill: string, stroke: string): void => {
    g.font = `${size}px ${DISPLAY_FONT}`;
    g.shadowColor = fill;
    g.shadowBlur = size * 0.35;
    g.lineWidth = size * 0.16;
    g.strokeStyle = stroke;
    g.strokeText(text, w / 2, y);
    g.shadowBlur = 0;
    g.fillStyle = fill;
    g.fillText(text, w / 2, y);
  };
  title('SHOVE OFF!', h * 0.36, Math.round(w * 0.15), '#FFFFFF', MAGENTA);
  title('PINBALL PANIC', h * 0.58, Math.round(w * 0.075), CYAN, '#15093A');
  // Bulbs around the frame (painted; the chase is in the shader via a mask in alpha).
  g.fillStyle = YELLOW;
  for (let i = 0; i < 22; i++) {
    const x = w * (0.08 + (i / 21) * 0.84);
    for (const y of [h * 0.12, h * 0.88]) {
      g.beginPath();
      g.arc(x, y, w * 0.009, 0, Math.PI * 2);
      g.fill();
    }
  }
}

// ---- Arena -----------------------------------------------------------------------------------

/** Static rails (and slingshots) as solids, exactly as the map module declares them. */
function staticRails(kit: ArenaKit): Solid[] {
  const out: Solid[] = Array.from({ length: 32 }, createSolid);
  const n = kit.probe.def.solids(kit.probe.world, out);
  return out.slice(0, n).filter((s) => s.kind === SolidKind.CAPSULE && (s.tag === SolidTag.NONE || s.tag === SolidTag.SLINGSHOT));
}

/** Pinball's view of the debug hooks (tests). */
export interface PinballDebug {
  lights(): { laneStep: number; arrowStep: number; rollStep: number; pulse: number; flipWarn: number; blink: number; launch: number; bump: number[] };
  flippers: InstancedMesh;
  balls: Mesh[];
}

export function buildPinball(kit: ArenaKit): Arena & { pinball: PinballDebug } {
  const root = new Group();
  root.name = 'arena:pinball';
  const owned: { dispose(): void }[] = [];
  const p = kit.palette;
  const env = kit.env;
  const rich = kit.rich;
  const ppm = kit.tier === 'high' ? 96 : kit.tier === 'medium' ? 80 : 64;
  const W = Math.round(PF.w * ppm);
  const H = Math.round(PF.h * ppm);
  const defs = insertDefs();
  const art = canvasTexture(W, H, (g, w, h) => paintPlayfield(g, w, h, ppm, defs));
  const ids = canvasTexture(W, H, (g, w, h) => paintIds(g, w, h, ppm, defs), false, false);
  owned.push(art, ids);

  // ---- Playfield slab ------------------------------------------------------------------------
  const u = {
    uArt: { value: art as Texture }, uIds: { value: ids as Texture }, uLaneStep: { value: 0 }, uArrowStep: { value: 0 }, uRollStep: { value: 0 },
    uPulse: { value: 0 }, uFlipWarn: { value: 0 }, uLaunch: { value: 0 }, uBump: { value: [0, 0, 0, 0, 0] },
    uBalls: { value: { x: 0, y: 0, z: 0, w: 0 } }, uBallOn: { value: { x: 0, y: 0 } },
  };
  const pfMat = surfaceMaterial(env, kit.ramp, {
    key: 'pinball-playfield', color: '#FFFFFF', rim: 0.25, gloss: 0.35, glossEdge: 0.97, pars: PLAYFIELD_PARS,
    albedo: PLAYFIELD_ALBEDO, emissive: PLAYFIELD_EMISSIVE, uniforms: u as never,
  });
  const tableShape = polygonShape(TUNING.table);
  const slabGeo = slab(tableShape, 0, SLAB_DEPTH, 0.04, 2, 2);
  const table = new Mesh(slabGeo, pfMat);
  table.name = 'playfield';
  table.receiveShadow = true;
  root.add(table);
  owned.push(slabGeo, pfMat);
  kit.batch.add(B.hullBold, slabGeo.clone(), null);

  // ---- Cabinet, legs and backbox (static) ----------------------------------------------------
  const cab = kit.batch;
  const inset = TUNING.table.map((q) => ({ x: q.x * 0.97, y: q.y * 0.97 }));
  cab.add(B.toon, slab(polygonShape(inset), -SLAB_DEPTH, CABINET_DEPTH, 0.08, 2, 2), null, { color: '#241A55', ao: [-SLAB_DEPTH - CABINET_DEPTH, -SLAB_DEPTH, 0.55] });
  // Neon side stripes on the cabinet.
  const stripe = slab(polygonShape(TUNING.table.map((q) => ({ x: q.x * 0.975, y: q.y * 0.975 }))), -SLAB_DEPTH - 0.5, 0.08, 0, 2, 1);
  cab.add(B.glow, stripe, null, { color: MAGENTA });
  const stripe2 = slab(polygonShape(TUNING.table.map((q) => ({ x: q.x * 0.975, y: q.y * 0.975 }))), -SLAB_DEPTH - 1.2, 0.05, 0, 2, 1);
  cab.add(B.glow, stripe2, null, { color: CYAN });
  for (const [x, y] of [[-8.1, -5.2], [8.1, -5.2], [-7.6, 2.9], [7.6, 2.9]] as const) {
    const leg = new CylinderGeometry(0.32, 0.22, 9, kit.seg(12, 6));
    cab.add(B.gloss, leg, new Matrix4().makeTranslation(x, -SLAB_DEPTH - CABINET_DEPTH - 4.5, y), { color: '#B8C4D6' });
    const foot = new CylinderGeometry(0.5, 0.5, 0.3, kit.seg(12, 6));
    cab.add(B.gloss, foot, new Matrix4().makeTranslation(x, -SLAB_DEPTH - CABINET_DEPTH - 0.1, y), { color: '#B8C4D6' });
  }
  // Backbox: the head of the machine behind the top rail, leaning back.
  const head = new Group();
  head.name = 'backbox';
  head.position.set(0, -2.6, -8.4);
  head.rotation.x = -0.2;
  root.add(head);
  const hb = new Batch();
  hb.add(B.toon, new RoundedBoxGeometry(16.2, 8.6, 1.3, 3, 0.3), new Matrix4().makeTranslation(0, 4.3, 0), { color: '#21184F', ao: [0, 2.5, 0.6] });
  hb.add(B.glow, new RoundedBoxGeometry(16.5, 0.18, 1.32, 1, 0.08), new Matrix4().makeTranslation(0, 8.55, 0), { color: CYAN });
  hb.add(B.glow, new RoundedBoxGeometry(16.5, 0.14, 1.32, 1, 0.06), new Matrix4().makeTranslation(0, 0.1, 0), { color: MAGENTA });
  // Speaker panel with the score reel, below the backglass.
  hb.add(B.toon, new RoundedBoxGeometry(9.2, 1.9, 0.3, 2, 0.12), new Matrix4().makeTranslation(0, 1.25, 0.72), { color: '#120B2E' });
  for (const s of [-1, 1]) {
    const grill = new CylinderGeometry(0.62, 0.62, 0.1, kit.seg(20, 10));
    grill.rotateX(Math.PI / 2);
    hb.add(B.toon, grill, new Matrix4().makeTranslation(s * 3.6, 1.25, 0.88), { color: '#3A2A7A' });
  }
  if (rich) hb.add(B.hull, new RoundedBoxGeometry(16.2, 8.6, 1.3, 1, 0.3), new Matrix4().makeTranslation(0, 4.3, 0));
  finishBatch(kit, hb, head, { castShadow: false });
  const glassTex = canvasTexture(1024, 448, paintBackglass);
  owned.push(glassTex);
  const glassMat = new MeshBasicMaterial({ map: glassTex, toneMapped: false, fog: true });
  const glass = new Mesh(new PlaneGeometry(14.6, 6.4), glassMat);
  glass.position.set(0, 5.15, 0.66);
  glass.name = 'backglass';
  head.add(glass);
  owned.push(glass.geometry, glassMat);
  const dmd = liveTexture(DMD_W, DMD_H);
  owned.push(dmd);
  const dmdMat = new ShaderMaterial({
    uniforms: { ...UniformsUtils.clone(UniformsLib.fog), uMap: { value: dmd.texture }, uTime: env.uTime, uHot: { value: 0 } },
    vertexShader: SIMPLE_VERTEX, fragmentShader: DMD_FRAGMENT, fog: true,
  });
  const dmdMesh = new Mesh(new PlaneGeometry(5.6, 1.25), dmdMat);
  dmdMesh.position.set(0, 1.25, 0.9);
  dmdMesh.name = 'score-reel';
  head.add(dmdMesh);
  owned.push(dmdMesh.geometry, dmdMat);
  const feed: string[] = [];
  let dmdHot = 0;
  const showReel = (top: string, bottom: string, hot: boolean): void => dmd.paint((g, w, h) => paintDmd(g, w, h, top, bottom, hot));
  showReel('PINBALL PANIC', 'INSERT BEAN', false);

  // ---- Rails, gap caps and slingshots -----------------------------------------------------------
  const rails = staticRails(kit);
  const railParts: BufferGeometry[] = [];
  const railHull: BufferGeometry[] = [];
  const RAIL_Y = TUNING.rail.top - TUNING.rail.radius;
  const gapEnds: { x: number; y: number }[] = [];
  const isGapEnd = (x: number, y: number): boolean => (Math.abs(y + 6) < 0.01 && Math.abs(Math.abs(x) - TUNING.topGapHalf) < 0.01)
    || (Math.abs(Math.abs(x) - 9) < 0.01 && Math.abs(Math.abs(y) - TUNING.sideGapHalf) < 0.01)
    || (Math.abs(y - 6) < 0.01 && Math.abs(Math.abs(x) - 3.2) < 0.01);
  for (const s of rails) {
    const dx = s.bx - s.ax;
    const dy = s.by - s.ay;
    const len = Math.hypot(dx, dy);
    const yaw = -Math.atan2(dy, dx);
    const mid = new Matrix4().makeRotationY(yaw).setPosition((s.ax + s.bx) / 2, RAIL_Y, (s.ay + s.by) / 2);
    if (s.tag === SolidTag.SLINGSHOT) {
      // Rubber band between two chrome posts.
      const band = new CylinderGeometry(s.r, s.r, len, kit.seg(14, 8));
      band.rotateZ(Math.PI / 2);
      cab.add(B.toon, band, new Matrix4().makeRotationY(yaw).setPosition((s.ax + s.bx) / 2, 0.32, (s.ay + s.by) / 2), { color: '#FFF0FA' });
      cab.add(B.glow, new CylinderGeometry(0.03, 0.03, len * 0.92, 4).rotateZ(Math.PI / 2), new Matrix4().makeRotationY(yaw).setPosition((s.ax + s.bx) / 2, 0.47, (s.ay + s.by) / 2), { color: MAGENTA });
      if (rich) cab.add(B.hull, new CylinderGeometry(s.r, s.r, len, 10).rotateZ(Math.PI / 2), new Matrix4().makeRotationY(yaw).setPosition((s.ax + s.bx) / 2, 0.32, (s.ay + s.by) / 2));
      for (const [x, y] of [[s.ax, s.ay], [s.bx, s.by]]) {
        const post = new CylinderGeometry(0.17, 0.2, TUNING.rail.top, kit.seg(14, 8));
        railParts.push(post.translate(x, TUNING.rail.top / 2, y));
        cab.add(B.glow, new SphereGeometry(0.12, 10, 8), new Matrix4().makeTranslation(x, TUNING.rail.top + 0.05, y), { color: isGapEnd(x, y) ? DANGER : CYAN });
        if (isGapEnd(x, y)) gapEnds.push({ x, y });
      }
      continue;
    }
    const bar = new CylinderGeometry(s.r, s.r, len, kit.seg(14, 8));
    bar.rotateZ(Math.PI / 2);
    railParts.push(bar.applyMatrix4(mid));
    railHull.push(new CylinderGeometry(s.r, s.r, len + s.r * 2, 10).rotateZ(Math.PI / 2).applyMatrix4(mid));
    const posts = Math.max(1, Math.round(len / 1.6));
    for (let k = 0; k <= posts; k++) {
      const t = k / posts;
      const x = s.ax + dx * t;
      const y = s.ay + dy * t;
      railParts.push(new CylinderGeometry(0.07, 0.09, RAIL_Y, 8).translate(x, RAIL_Y / 2, y));
    }
    for (const [x, y] of [[s.ax, s.ay], [s.bx, s.by]]) {
      const end = new SphereGeometry(s.r * 1.25, kit.seg(12, 8), kit.seg(10, 6));
      if (isGapEnd(x, y)) {
        cab.add(B.glow, end, new Matrix4().makeTranslation(x, RAIL_Y, y), { color: DANGER });
        gapEnds.push({ x, y });
      } else railParts.push(end.translate(x, RAIL_Y, y));
    }
  }
  const railGeo = mergeKeep(railParts, ['position', 'normal']);
  const neonEnv = canvasTexture(512, 256, paintNeonEnv);
  neonEnv.mapping = EquirectangularReflectionMapping;
  owned.push(neonEnv);
  const chrome = new MeshStandardMaterial({ color: '#98A4BC', metalness: 1, roughness: 0.38, envMap: neonEnv, envMapIntensity: 0.8 });
  owned.push(chrome);
  const railMesh = new Mesh(railGeo, chrome);
  railMesh.name = 'rails';
  railMesh.castShadow = true;
  root.add(railMesh);
  owned.push(railGeo);
  if (railHull.length > 0) {
    const hullSrc = mergeKeep(railHull, ['position', 'normal']);
    const rh = new Mesh(hullGeometry(hullSrc), kit.mats.hull);
    hullSrc.dispose();
    rh.name = 'rails-outline';
    root.add(rh);
    owned.push(rh.geometry);
  }

  // ---- Pop bumpers (instanced) ------------------------------------------------------------------
  const BP = TUNING.bumper;
  const bumperGeo = bumperGeometry(kit);
  const tints = new Float32Array(BP.positions.length * 3);
  const flashes = new Float32Array(BP.positions.length);
  const tintAttr = new InstancedBufferAttribute(tints, 3);
  const flashAttr = new InstancedBufferAttribute(flashes, 1);
  flashAttr.setUsage(DynamicDrawUsage);
  BP.positions.forEach((_, i) => {
    const c = new Color(i % 2 === 0 ? MAGENTA : CYAN);
    tints.set([c.r, c.g, c.b], i * 3);
  });
  bumperGeo.setAttribute('aTint', tintAttr);
  bumperGeo.setAttribute('aFlash', flashAttr);
  const bumperMat = surfaceMaterial(env, kit.ramp, {
    key: 'pinball-bumper', vertexColors: true, rim: 0.4, gloss: 0.55, glossEdge: 0.96,
    pars: 'varying vec3 vTint;\nvarying float vFlash;\nvarying float vPart;\nuniform float uBumpPulse;\n',
    vertexPars: 'attribute vec3 aTint;\nattribute float aFlash;\nattribute float aPart;\n',
    vertexEnd: 'vTint = aTint; vFlash = aFlash; vPart = aPart;',
    uniforms: { uBumpPulse: u.uPulse },
    albedo: /* glsl */ `
      if (vPart > 0.5 && vPart < 1.5) albedo *= vTint;
      if (vPart > 2.5) {
        // Translucent tinted dome with a white star on top.
        float star = 1.0 - smoothstep(0.16, 0.2, length(envObj.xz) + 0.04 * sin(atan(envObj.z, envObj.x) * 5.0));
        albedo = mix(mix(vec3(1.0), vTint, 0.72), vec3(1.0), star * step(1.35, envObj.y));
      }
      albedo = mix(albedo, vec3(1.0), vFlash * 0.8);`,
    emissive: /* glsl */ `
      if (vPart > 1.5 && vPart < 2.5) glow += mix(vTint, vec3(1.0), vFlash) * (0.7 + 0.8 * uBumpPulse + 2.2 * vFlash);
      if (vPart > 0.5 && vPart < 1.5) glow += vTint * (0.12 + 0.25 * uBumpPulse + 0.8 * vFlash);
      if (vPart > 2.5) glow += vTint * (0.06 + 0.18 * uBumpPulse) + vec3(1.0) * vFlash * 0.6;`,
  });
  const bumpers = new InstancedMesh(bumperGeo, bumperMat, BP.positions.length);
  bumpers.name = 'bumpers';
  bumpers.castShadow = true;
  bumpers.receiveShadow = true;
  bumpers.instanceMatrix.setUsage(DynamicDrawUsage);
  bumpers.frustumCulled = false;
  bumpers.userData.hazard = true;
  root.add(bumpers);
  owned.push(bumperGeo, bumperMat);
  const bumperHullSrc = sweep([[[0, 0], [BP.radius + 0.04, 0], [BP.radius + 0.04, 0.12], [BP.radius - 0.02, 0.2], [BP.radius - 0.08, 0.95], [0.74, 1.05], [0.66, 1.3], [0.3, 1.48], [0, BP.top]]], 20);
  const bumperHullGeo = hullGeometry(bumperHullSrc);
  bumperHullSrc.dispose();
  const bumperHull = new InstancedMesh(bumperHullGeo, outlineMaterial(0.035, kit.outlineScale), BP.positions.length);
  bumperHull.instanceMatrix = bumpers.instanceMatrix;
  bumperHull.frustumCulled = false;
  bumperHull.name = 'bumpers-outline';
  root.add(bumperHull);
  owned.push(bumperHullGeo, bumperHull.material as ShaderMaterial);
  const bumpFlash = new Float32Array(BP.positions.length);

  // ---- "+100" score pops over bumpers (§11.4): neon billboards, one draw call -------------------
  const POPS = 8;
  const popTex = canvasTexture(256, 128, (g, w, h) => {
    g.clearRect(0, 0, w, h);
    g.font = `${Math.round(h * 0.62)}px ${DISPLAY_FONT}`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.lineJoin = 'round';
    g.shadowColor = MAGENTA;
    g.shadowBlur = h * 0.18;
    g.lineWidth = h * 0.16;
    g.strokeStyle = MAGENTA;
    g.strokeText('+100', w / 2, h / 2 + 4);
    g.shadowBlur = 0;
    g.lineWidth = h * 0.07;
    g.strokeStyle = '#FFFFFF';
    g.strokeText('+100', w / 2, h / 2 + 4);
    g.fillStyle = CYAN;
    g.fillText('+100', w / 2, h / 2 + 4);
  });
  owned.push(popTex);
  const popGeo = new PlaneGeometry(1.6, 0.8);
  const popData = new Float32Array(POPS * 2);
  const popAttr = new InstancedBufferAttribute(popData, 2).setUsage(DynamicDrawUsage);
  popGeo.setAttribute('aPop', popAttr);
  const popMat = new ShaderMaterial({
    uniforms: { uMap: { value: popTex }, uTime: env.uTime },
    vertexShader: /* glsl */ `
      attribute vec2 aPop;   // x = birth time, y = 1 when alive
      uniform float uTime;
      varying vec2 vUv;
      varying float vA;
      void main() {
        vUv = uv;
        float age = uTime - aPop.x;
        float k = clamp(age / 0.85, 0.0, 1.0);
        float pop = k < 0.15 ? mix(0.4, 1.25, k / 0.15) : mix(1.25, 1.0, clamp((k - 0.15) / 0.2, 0.0, 1.0));
        vA = aPop.y * (1.0 - smoothstep(0.65, 1.0, k)) * step(0.0, age);
        vec4 c = modelViewMatrix * instanceMatrix * vec4(0.0, 1.6 * (1.0 - pow(1.0 - k, 2.0)), 0.0, 1.0);
        c.xy += position.xy * pop;
        gl_Position = projectionMatrix * c;
        // Idle or finished pops never rasterize (they only discarded in the fragment shader).
        if (vA <= 0.0) gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      uniform sampler2D uMap;
      varying vec2 vUv;
      varying float vA;
      void main() {
        vec4 t = texture2D(uMap, vUv);
        float a = t.a * vA;
        if (a < 0.01) discard;
        gl_FragColor = vec4(t.rgb * a * 1.3, a);
        #include <colorspace_fragment>
      }`,
    transparent: true, depthWrite: false, depthTest: false, premultipliedAlpha: true, toneMapped: false,
  });
  const scorePops = new InstancedMesh(popGeo, popMat, POPS);
  scorePops.name = 'score-pops';
  scorePops.frustumCulled = false;
  scorePops.renderOrder = 20;
  scorePops.instanceMatrix.setUsage(DynamicDrawUsage);
  root.add(scorePops);
  owned.push(popGeo, popMat);
  let nextPop = 0;
  const popM = new Matrix4();
  function scorePop(x: number, y: number, now: number): void {
    const i = nextPop;
    nextPop = (nextPop + 1) % POPS;
    popM.makeTranslation(x + (Math.random() - 0.5) * 0.4, TUNING.bumper.top + 0.3, y);
    scorePops.setMatrixAt(i, popM);
    scorePops.instanceMatrix.needsUpdate = true;
    popData[i * 2] = now;
    popData[i * 2 + 1] = 1;
    popAttr.needsUpdate = true;
  }

  // ---- Flippers (instanced: body, outline, neon edge, afterimage) -----------------------------
  const flipGeo = flipperGeometry(kit);
  const flipMat = surfaceMaterial(env, kit.ramp, { key: 'pinball-flipper', vertexColors: true, rim: 0.35, gloss: 0.7, glossEdge: 0.93 });
  const flippers = new InstancedMesh(flipGeo, flipMat, 2);
  flippers.name = 'flippers';
  flippers.castShadow = true;
  flippers.receiveShadow = true;
  flippers.instanceMatrix.setUsage(DynamicDrawUsage);
  flippers.frustumCulled = false;
  flippers.userData.hazard = true;
  root.add(flippers);
  owned.push(flipGeo, flipMat);
  const flipHullSrc = flipperShapeGeometry(0.4, 0.36, 0);
  const flipHullGeo = hullGeometry(flipHullSrc);
  flipHullSrc.dispose();
  const flipHull = new InstancedMesh(flipHullGeo, outlineMaterial(0.04, kit.outlineScale), 2);
  flipHull.instanceMatrix = flippers.instanceMatrix;
  flipHull.frustumCulled = false;
  root.add(flipHull);
  owned.push(flipHullGeo, flipHull.material as ShaderMaterial);
  const edgeGeo = flipperEdgeGeometry();
  const edgeGlow = new Float32Array(2);
  const edgeAttr = new InstancedBufferAttribute(edgeGlow, 1);
  edgeAttr.setUsage(DynamicDrawUsage);
  edgeGeo.setAttribute('aGlow', edgeAttr);
  const edgeMat = new ShaderMaterial({
    uniforms: { uColor: { value: new Color(CYAN) }, uHot: { value: new Color(YELLOW) } },
    vertexShader: /* glsl */ `
      attribute float aGlow;
      varying float vGlow;
      void main() {
        vGlow = aGlow;
        gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      uniform vec3 uColor;
      uniform vec3 uHot;
      varying float vGlow;
      void main() {
        vec3 c = mix(uColor, uHot, clamp(vGlow - 1.0, 0.0, 1.0));
        gl_FragColor = vec4(c * (0.6 + 0.9 * min(vGlow, 2.0)), 1.0);
        #include <colorspace_fragment>
      }`,
    toneMapped: false,
  });
  const edges = new InstancedMesh(edgeGeo, edgeMat, 2);
  edges.instanceMatrix = flippers.instanceMatrix;
  edges.frustumCulled = false;
  edges.name = 'flipper-neon';
  edges.userData.hazardDecor = true;
  root.add(edges);
  owned.push(edgeGeo, edgeMat);
  const ghostMat = new MeshBasicMaterial({ color: CYAN, transparent: true, opacity: 0.0, blending: AdditiveBlending, depthWrite: false, toneMapped: false });
  const ghosts = new InstancedMesh(flipHullGeo, ghostMat, 2);
  ghosts.instanceMatrix.setUsage(DynamicDrawUsage);
  ghosts.frustumCulled = false;
  ghosts.name = 'flipper-afterimage';
  ghosts.userData.hazardDecor = true;
  // The afterimage is a High/Medium nicety; Low and Potato keep the draw call.
  if (kit.rich) root.add(ghosts);
  owned.push(ghostMat);

  // ---- Giant chrome pinballs --------------------------------------------------------------------
  // Roughness 0.2: at 0.08 the key light's specular blew past the bloom threshold into a white glare.
  const ballMat = new MeshStandardMaterial({ color: '#DCE2EC', metalness: 1, roughness: 0.2, envMap: neonEnv, envMapIntensity: 1.2 });
  const ballGeo = new SphereGeometry(BALL_R, kit.seg(48, 20), kit.seg(32, 14));
  const ballHullGeo = hullGeometry(new SphereGeometry(BALL_R, 24, 16));
  const blobMat = new MeshBasicMaterial({ map: kit.lib.blobShadowTexture, transparent: true, depthWrite: false, opacity: 0.55, color: '#000000', toneMapped: false });
  const blobGeo = new PlaneGeometry(BALL_R * 2.6, BALL_R * 2.6).rotateX(-Math.PI / 2);
  owned.push(ballMat, ballGeo, ballHullGeo, blobMat, blobGeo);
  const balls: Mesh[] = [];
  // Always-on blob shadows for the two balls (§12.3), one draw call.
  const ballBlobs = new InstancedMesh(blobGeo, blobMat, 2);
  ballBlobs.name = 'pinball-blobs';
  ballBlobs.renderOrder = 1;
  ballBlobs.frustumCulled = false;
  ballBlobs.instanceMatrix.setUsage(DynamicDrawUsage);
  ballBlobs.count = 0;
  root.add(ballBlobs);
  const blobM = new Matrix4();
  const ballState = [0, 1].map(() => ({ x: 0, y: 0, seen: false, q: new Quaternion(), pop: 0 }));
  for (let i = 0; i < 2; i++) {
    const ball = new Mesh(ballGeo, ballMat);
    ball.name = `pinball-${i}`;
    ball.castShadow = true;
    ball.visible = false;
    ball.userData.hazard = true;
    const hull = new Mesh(ballHullGeo, kit.mats.hull);
    ball.add(hull);
    root.add(ball);
    balls.push(ball);
  }

  // ---- Per-frame -------------------------------------------------------------------------------
  const m = new Matrix4();
  const q = new Quaternion();
  const axis = new Vector3();
  const s3 = new Vector3();
  const pos = new Vector3();
  const flipAngles = [0, 0];
  const flipHist: number[][] = [[0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0]];
  const UP = new Vector3(0, 1, 0);

  /** Sim angle of flipper i's axis (pivot → tip) for a swing `a` (radians up from rest). */
  function flipperAngle(i: number, a: number): number {
    const rest = F.restDeg * DEG;
    const ang = rest - Math.abs(a);
    return i === 0 ? ang : Math.PI - ang;
  }

  function writeFlipper(mesh: InstancedMesh, i: number, angle: number, scale = 1): void {
    const pv = F.pivots[i];
    q.setFromAxisAngle(UP, -angle);
    m.compose(pos.set(pv.x, 0, pv.y), q, s3.set(scale, scale, scale));
    mesh.setMatrixAt(i, m);
  }

  for (let i = 0; i < 2; i++) writeFlipper(flippers, i, flipperAngle(i, 0));
  BP.positions.forEach((b, i) => {
    bumpers.setMatrixAt(i, m.makeTranslation(b.x, 0, b.y));
  });

  let flipBlink = 0;
  const lightState = { laneStep: 0, arrowStep: 0, rollStep: 0, pulse: 0, flipWarn: 0, blink: 0, launch: 0, bump: bumpFlash as unknown as number[] };

  const layout: ArenaLayout = {
    sky: { top: p.skyTop, bottom: p.skyBottom, cloud: '#5A3A9E', clouds: 0, stars: 0.8, sunGlow: 0.25, synthSun: 1, synthColors: [MAGENTA, YELLOW] },
    voidColors: { a: '#0B0820', b: GRID, c: MAGENTA },
    voidEdge: 10,
    voidShadow: { x: 0, y: 0, hx: 8.5, hy: 5.5, r: 1.2, strength: 0.9 },
    splash: { ring: CYAN, edge: MAGENTA },
    audience: { cx: 0, cy: -0.5, rx: 15.5, ry: 11.5, y: -0.6, plank: '#2A1F5C', trim: CYAN, fur: ['#FF3EC8', '#2FF3FF', '#FFC93C', '#B9A4FF', '#FFFFFF', '#7FFFD4', '#FF8FB1'] },
    confetti: { colors: [MAGENTA, CYAN, YELLOW, GRID, '#FFFFFF'], r0: 18, r1: 40, y0: -2, y1: 18 },
    shadowY: [-1.0, 3.0],
  };

  return {
    root, layout,
    pinball: { lights: () => lightState, flippers, balls },
    update(f: ArenaFrame) {
      const v = f.view;
      const c = v.clock;
      const beat = f.beat;
      // §11.4 / §29.4: lights chase on the beat.
      lightState.laneStep = Math.floor(beat * 2);
      lightState.arrowStep = Math.floor(beat * 4) % 4;
      lightState.rollStep = Math.floor(beat) % 4;
      const frac = beat - Math.floor(beat);
      lightState.pulse = Math.pow(1 - frac, 3);
      // Flipper lights blink on the 2 beats before each fire, BALL LAUNCH flashes the plunger lane:
      // WP2's pose carries both telegraphs; without them they follow §6.3 from the clock.
      const hz = v.hazard;
      if (hz.flipperWarn !== undefined) {
        lightState.flipWarn = hz.flipperWarn > 0 ? 1 : 0;
        flipBlink = hz.flipperLights ? 1 : 0;
      } else {
        const every = (c.phase === RoundPhase.SUDDEN_DEATH ? 1 : 2) * BEATS_PER_BAR;
        const toFire = every - (beat - Math.floor(beat / every) * every);
        const playing = c.phase === RoundPhase.CALM || c.phase === RoundPhase.RAMP || c.phase === RoundPhase.SUDDEN_DEATH;
        lightState.flipWarn = playing && toFire <= 2 ? 1 : 0;
        flipBlink = frac < 0.5 ? 1 : 0;
      }
      lightState.blink = flipBlink;
      if (hz.ballWarn !== undefined) lightState.launch = hz.ballWarn > 0 ? 1 : 0;
      else {
        const barTicks = c.tpb * BEATS_PER_BAR;
        lightState.launch = Number.isFinite(c.rampStart) && c.tick >= c.rampStart - barTicks && c.tick < c.rampStart ? 1 : 0;
      }
      for (let i = 0; i < bumpFlash.length; i++) bumpFlash[i] = Math.max(0, bumpFlash[i] - f.dt * 4);
      u.uLaneStep.value = lightState.laneStep;
      u.uArrowStep.value = lightState.arrowStep;
      u.uRollStep.value = lightState.rollStep;
      u.uPulse.value = lightState.pulse;
      u.uFlipWarn.value = lightState.flipWarn * flipBlink;
      u.uLaunch.value = lightState.launch;
      for (let i = 0; i < 5; i++) (u.uBump.value as number[])[i] = bumpFlash[i];
      // Bumpers: flash white and scale 1.15 (§11.4).
      BP.positions.forEach((b, i) => {
        const k = 1 + 0.15 * bumpFlash[i] * bumpFlash[i];
        m.makeScale(k, k, k).setPosition(b.x, 0, b.y);
        bumpers.setMatrixAt(i, m);
        flashes[i] = bumpFlash[i];
      });
      bumpers.instanceMatrix.needsUpdate = true;
      flashAttr.needsUpdate = true;
      // Flippers from the hazard pose; the neon edge blinks with the telegraph and flares on the swing.
      for (let i = 0; i < 2; i++) {
        const a = v.hazard.flipperAngles[i];
        flipAngles[i] = a;
        writeFlipper(flippers, i, flipperAngle(i, a));
        const hist = flipHist[i];
        hist.pop();
        hist.unshift(a);
        const lag = hist[3];
        writeFlipper(ghosts, i, flipperAngle(i, lag), 1.02);
        const swinging = Math.abs(a) > 0.02;
        edgeGlow[i] = swinging ? 2 : lightState.flipWarn > 0 ? (flipBlink > 0 ? 1.6 : 0.3) : 0.5;
      }
      ghostMat.opacity = Math.min(0.55, Math.abs(flipAngles[0] - flipHist[0][3]) * 2.5);
      flippers.instanceMatrix.needsUpdate = true;
      ghosts.instanceMatrix.needsUpdate = true;
      edgeAttr.needsUpdate = true;
      // Giant balls: position from the pose, rolling rotation from the motion.
      const bv = v.hazard.balls;
      let blobs = 0;
      for (let i = 0; i < 2; i++) {
        const b = bv[i];
        const st = ballState[i];
        const ball = balls[i];
        if (!b || !b.active) {
          ball.visible = false;
          st.seen = false;
          continue;
        }
        if (!st.seen) {
          st.seen = true;
          st.x = b.x;
          st.y = b.y;
          st.pop = 0;
        }
        const dx = b.x - st.x;
        const dy = b.y - st.y;
        const d = Math.hypot(dx, dy);
        if (d > 1e-5 && d < 3) {
          axis.set(dy, 0, -dx).normalize();
          // Rolls about up × direction of travel.
          q.setFromAxisAngle(axis, d / BALL_R);
          st.q.premultiply(q);
        }
        st.x = b.x;
        st.y = b.y;
        st.pop = Math.min(1, st.pop + f.dt * 5);
        const k = st.pop < 1 ? 1 - Math.pow(1 - st.pop, 3) * Math.cos(st.pop * 9) : 1;
        ball.visible = true;
        ball.position.set(b.x, BALL_R * k, b.y);
        ball.quaternion.copy(st.q);
        ball.scale.setScalar(Math.max(0.01, k));
        ballBlobs.setMatrixAt(blobs++, blobM.makeTranslation(b.x, 0.012, b.y));
      }
      ballBlobs.count = blobs;
      ballBlobs.instanceMatrix.needsUpdate = true;
      u.uBalls.value = { x: bv[0]?.x ?? 0, y: bv[0]?.y ?? 0, z: bv[1]?.x ?? 0, w: bv[1]?.y ?? 0 };
      u.uBallOn.value = { x: bv[0]?.active ? 1 : 0, y: bv[1]?.active ? 1 : 0 };
      dmdHot = Math.max(0, dmdHot - f.dt * 0.6);
      dmdMat.uniforms.uHot.value = dmdHot > 0.8 ? (Math.sin(f.time * 30) > 0 ? 1 : 0) : 0;
    },
    onEvent(ev, f) {
      if (ev.type === EV.BUMPER) {
        const i = ev.c;
        if (i >= 0 && i < bumpFlash.length) {
          bumpFlash[i] = 1;
          const b = BP.positions[i];
          kit.particles.burst({ x: b.x, y: 1.1, z: b.y, count: 8, color: [i % 2 === 0 ? MAGENTA : CYAN, '#FFFFFF'], speed: 4.5, up: 3.5, size: 0.06, life: 0.6, spread: 0.6, gravity: 10 });
          scorePop(b.x, b.y, f.time);
        }
      } else if (ev.type === EV.KO) {
        const v = f.view;
        const victim = v.beans[ev.a]?.name ?? 'BEAN';
        const verb = VERBS[ev.c] ?? 'BONKED';
        const credit = ev.b < v.beans.length && ev.b !== ev.a ? v.beans[ev.b]?.name ?? '' : '';
        feed.unshift(credit ? `${credit.toUpperCase()} ${verb} ${victim.toUpperCase()}` : `${victim.toUpperCase()} ${verb}!`);
        feed.length = Math.min(feed.length, 3);
        showReel(feed[0], feed[1] ?? 'PINBALL PANIC', true);
        dmdHot = 1.6;
      } else if (ev.type === EV.PHASE && ev.b === RoundPhase.RAMP) {
        showReel('BALL LAUNCH!', 'HAZARDS UP', true);
        dmdHot = 1.6;
      } else if (ev.type === EV.PHASE && ev.b === RoundPhase.SUDDEN_DEATH) {
        showReel('MULTIBALL!', 'SUDDEN DEATH', true);
        dmdHot = 1.6;
      }
    },
    debugHazards() {
      const out: DebugHazard[] = [];
      for (let i = 0; i < 2; i++) {
        const pv = F.pivots[i];
        const a = flipperAngle(i, flipAngles[i]);
        out.push({
          kind: 'flipper', index: i, a: new Vector3(pv.x, F.top / 2, pv.y),
          b: new Vector3(pv.x + Math.cos(a) * F.length, F.top / 2, pv.y + Math.sin(a) * F.length), radius: F.radius,
        });
      }
      balls.forEach((b, i) => {
        if (b.visible) out.push({ kind: 'ball', index: i, a: b.position.clone(), b: b.position.clone(), radius: BALL_R });
      });
      return out;
    },
    dispose() {
      for (const o of owned) o.dispose();
      void gapEnds;
    },
  };
}

/** A pop bumper: black base, tinted skirt, glowing ring, white star cap (aPart 0 fixed, 1 tinted, 2 ring, 3 cap). */
function bumperGeometry(kit: ArenaKit): BufferGeometry {
  const r = TUNING.bumper.radius;
  const top = TUNING.bumper.top;
  const seg = kit.seg(32, 14);
  const parts: { g: BufferGeometry; part: number; color: string }[] = [];
  const base: ProfilePoint[] = [[0, 0.001], [r + 0.04, 0.001]];
  parts.push({ g: sweep([base, [[r + 0.04, 0.001], [r + 0.04, 0.12], [r - 0.02, 0.2]]], seg), part: 0, color: '#1A1433' });
  parts.push({ g: sweep([[[r - 0.02, 0.2], [r - 0.08, 0.6], [r - 0.06, 0.92]]], seg), part: 1, color: '#FFFFFF' });
  const ring = new TorusGeometry(0.74, 0.085, kit.seg(10, 6), seg);
  ring.rotateX(Math.PI / 2);
  ring.translate(0, 0.98, 0);
  parts.push({ g: ring, part: 2, color: '#FFFFFF' });
  parts.push({ g: sweep([[[0.74, 1.05], [0.7, 1.22], [0.55, 1.4], [0.3, 1.48], [0, top]]], seg), part: 3, color: '#FFFFFF' });
  const star = new CylinderGeometry(0.22, 0.22, 0.02, 5);
  star.translate(0, top + 0.005, 0);
  parts.push({ g: star, part: 1, color: '#FFFFFF' });
  const merged: BufferGeometry[] = [];
  const col = new Color();
  for (const { g, part, color } of parts) {
    const n = g.index ? g.toNonIndexed() : g;
    if (n !== g) g.dispose();
    for (const k of Object.keys(n.attributes)) if (k !== 'position' && k !== 'normal') n.deleteAttribute(k);
    const count = n.getAttribute('position').count;
    col.set(color);
    const c = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) c.set([col.r, col.g, col.b], i * 3);
    n.setAttribute('color', new Float32BufferAttribute(c, 3));
    n.setAttribute('aPart', new Float32BufferAttribute(new Float32Array(count).fill(part), 1));
    merged.push(n);
  }
  return mergeKeep(merged, ['position', 'normal', 'color', 'aPart']);
}

/** The flipper outline in its local frame (+x from the pivot to the tip): a tapered capsule. */
function flipperShape(grow = 0): Shape {
  const r0 = 0.3 + grow;
  const r1 = 0.15 + grow;
  const L = F.length;
  const s = new Shape();
  const a = Math.asin((r0 - r1) / L);
  s.absarc(0, 0, r0, Math.PI / 2 - a, Math.PI * 1.5 + a, false);
  s.absarc(L, 0, r1, -Math.PI / 2 + a, Math.PI / 2 - a, false);
  s.closePath();
  return s;
}

function flipperShapeGeometry(top: number, depth: number, grow: number): BufferGeometry {
  const g = new ExtrudeGeometry(flipperShape(grow), { depth, bevelEnabled: true, bevelThickness: 0.04, bevelSize: 0.04, bevelSegments: 2, curveSegments: 10 });
  // Shape XY = (along, sideways); extrusion along +Z → three.js: x = along, z = sideways, y = up.
  g.applyMatrix4(new Matrix4().set(1, 0, 0, 0, 0, 0, 1, top - depth - 0.04, 0, -1, 0, 0, 0, 0, 0, 1));
  g.computeVertexNormals();
  return g;
}

/** Flipper body: white top, magenta rubber sides, a chrome pivot cap. */
function flipperGeometry(kit: ArenaKit): BufferGeometry {
  const shaped = flipperShapeGeometry(F.top, 0.34, 0);
  const body = shaped.index ? shaped.toNonIndexed() : shaped;
  const pos = body.getAttribute('position');
  const nrm = body.getAttribute('normal');
  const colors = new Float32Array(pos.count * 3);
  const white = new Color('#F6F0FF');
  const rubber = new Color(MAGENTA);
  for (let i = 0; i < pos.count; i++) {
    const c = nrm.getY(i) > 0.6 ? white : rubber;
    colors.set([c.r, c.g, c.b], i * 3);
  }
  body.setAttribute('color', new Float32BufferAttribute(colors, 3));
  body.deleteAttribute('uv');
  const cap = new CylinderGeometry(0.14, 0.16, 0.08, kit.seg(14, 8)).translate(0, F.top + 0.02, 0).toNonIndexed();
  cap.deleteAttribute('uv');
  const cc = new Float32Array(cap.getAttribute('position').count * 3);
  const chromeC = new Color('#C9D3E6');
  for (let i = 0; i < cc.length; i += 3) cc.set([chromeC.r, chromeC.g, chromeC.b], i);
  cap.setAttribute('color', new Float32BufferAttribute(cc, 3));
  return mergeKeep([body, cap], ['position', 'normal', 'color']);
}

/** The neon edge: a thin ring around the flipper's top face. */
function flipperEdgeGeometry(): BufferGeometry {
  const outer = flipperShape(0.03);
  const inner = flipperShape(-0.06);
  outer.holes.push(inner);
  const g = new ExtrudeGeometry(outer, { depth: 0.02, bevelEnabled: false, curveSegments: 10 });
  g.applyMatrix4(new Matrix4().set(1, 0, 0, 0, 0, 0, 1, F.top + 0.005, 0, -1, 0, 0, 0, 0, 0, 1));
  return g;
}

void DoubleSide;
